import { Injectable, signal } from '@angular/core';
import  Keycloak from 'keycloak-js';
import { BehaviorSubject, Subject } from 'rxjs';
import { USER_MGMT_CNST } from '../constants/proj.cnst';
import { PROJECT_CONSTANTS } from '../constants/proj.cnst'; 
import { isEmptyObject } from '../shared/common-utils';
import { readLocalUserPermissions } from '../shared/permission.util';

/**
  * NOTE: Use this service as a centralized data store.
  * Data which can be shared globally across multiple components that you can keep here.
  * Use subjects only if it is needed.
  * Write setters and getters for each variable. all variables except subjects should be private.
  *
  * EXAMPLE:
  * private sidebarState: "opened" | "closed" = "closed";
  * sidebarStateRef = new BehaviorSubject<"opened" | "closed">(this.sidebarState);
  *
  * getSidebarState() {return this.sidebarState;}
  * setSidebarState(state: "opened" | "closed") {
  *  this.sidebarState = sidebarState;
  *  this.sidebarStateRef.next(this.sidebarState);
  * }
  */
@Injectable({
  providedIn: 'root'
})
export class AppStateService {
  constructor() { }

  public userProfile: any = null;

  allRecordsFetched: boolean = false;
  allRecordsFetchedRef = new Subject<boolean>();

  setAllRecordsFetched(value: boolean) {
    this.allRecordsFetched = value;
    this.allRecordsFetchedRef.next(this.allRecordsFetched);
  }

  getAllRecordsFetched() {
    return this.allRecordsFetched;
  }


  //*Infinite Scrolling
  wasPageScrolled: boolean = false;
  wasPageScrolledRef = new Subject<boolean>();

  setWasPageScrolled(value: boolean) {
    this.wasPageScrolled = value;
    this.wasPageScrolledRef.next(this.wasPageScrolled);
  }

  getWasPageScrolled() {
    return this.wasPageScrolled;
  }

  urlDetails: any = {};
  urlDetailsRef = new Subject<any[]>();

  setUrlDetails(value: any) {
    this.urlDetails = value;
    this.urlDetailsRef.next(this.urlDetails);
  }

  getUrlDetails() {
    return this.urlDetails;
  }

  showSidebar: boolean = true;
  showSidebarRef = new Subject<boolean>();

  getSidebarState() {
    return this.showSidebar;
  }

  setSidebarState(state: boolean) {
    this.showSidebar = state;
    this.showSidebarRef.next(this.showSidebar);
  }

  //*Add, Edit, Delete Operations
  operation: any = {};
  operationRef = new Subject<any>();

  setOperationRef(data: any) {
    this.operation = data;
    this.operationRef.next(this.operation);
  }

  userPermissions: any = {};
  userPermissionsRef = new Subject<any>();

  setUserPermissionsRef(data: any) {
    this.userPermissions = data;
    this.userPermissionsRef.next(this.userPermissions);
  }

  getUserPermissions() : any {
    if(!isEmptyObject(this.userPermissions)) {
      return this.userPermissions;
    }
    return readLocalUserPermissions();
  }

  userMetaInfo: any = {};
  userMetaInfoRef = new Subject<any>();

  setUserMetaInfo(data: any) {
    this.userMetaInfo = data;
    this.userMetaInfoRef.next(this.userMetaInfo);
  }

  getUserMetaInfo() : any {
    return this.userMetaInfo;
  }

  inProcess : boolean = false;
  permissionsLoaded : boolean = false;

  setInProcess(value : boolean) {
    this.inProcess = value;
  }

  setPermissionsLoaded(value : boolean) {
    this.permissionsLoaded = value;
  }

  getInProcess() : boolean {
    return this.inProcess;
  }

  getPermissionsLoaded() : boolean {
    return this.permissionsLoaded;
  }

  customerData: any = null;

  setCustomerData(value: any){
    this.customerData = value;
  }

  getCustomerData(){
    return this.customerData;
  }

  profileData: any = null;

  setProfileData(value: any){
    this.profileData = value;
  }

  getProfileData(){
    return this.profileData;
  }

  applicationData: any = null;

  setApplicationData(value: any){
    this.applicationData = value;
  }

  getApplicationData(){
    return this.applicationData;
  }

  currentCustomerId : any = null;

  setCurrentCustomerId(customerId : string) {
    this.currentCustomerId = customerId;
  }

  getCurrentCustomerId() : string {
    return this.currentCustomerId;
  }

  currentResourceOperation : any = null;

  setCurrentResourceOperation(operationType : string) {
    this.currentResourceOperation = operationType;
  }

  getCurrentResourceOperation() : string {
    return this.currentResourceOperation;
  }

  currentCustomerDetails : any = {};

  setCurrentCustomerInfo(customerDetails : any) {
    this.currentCustomerDetails = customerDetails;
  }

  getCurrentCustomerInfo() : any {
    return this.currentCustomerDetails;
  }

  selectedResourceDetails : any = {};

  setSelectedResourceDetails(selectedResourceDetails : any) {
    this.selectedResourceDetails = selectedResourceDetails;
  }

  getSelectedResourceDetails() : any {
    return this.selectedResourceDetails;
  }

  deletedAppInfo : any = {};
  deletedAppInfoRef = new Subject<any>();

  setDeletedAppInfoDetails(appInfo : any) {
    this.deletedAppInfo = appInfo;
    this.deletedAppInfoRef.next(this.deletedAppInfo);
  }

  getDeletedAppInfoDetails() : any {
    return this.deletedAppInfo;
  }

  isTokensLoaded : boolean = false;
  tokensLoadedRef = new Subject<boolean>();

  setIsTokensLoaded(status : boolean) {
    this.isTokensLoaded = status;
    this.tokensLoadedRef.next(this.isTokensLoaded);
  }

  keyCloakInstance!: Keycloak;

  setKeyCloakInstance(instance : Keycloak) {
    this.keyCloakInstance = instance;
    console.log("instancee==", this.keyCloakInstance)
  }

  getKeyCloakInstance() : Keycloak {
    return this.keyCloakInstance;
  }

  currentResourceName : any = null;
  currentResourceNameRef = new Subject<string>();

  setCurrentResourceName(resourceName : string) : void {
    this.currentResourceName = resourceName;
    this.currentResourceNameRef.next(this.currentResourceName);
  }

  getCurrentResourceName() : string {
    return this.currentResourceName;
  }

  getCurrentOrgDetails() {
    return JSON.parse(localStorage.getItem('currentOrg') ?? '{}')
  }
  
  getCustomerDetails() {
    return JSON.parse(localStorage.getItem('customerInfo') ?? '{}')
  }

  setLoggedInUserType(userPermissions: any) {
    const { meta, permissions } = userPermissions || {};
    const userTypeKey = USER_MGMT_CNST.LOGGED_IN_USER_TYPE_KEY;
    const userTypes = USER_MGMT_CNST.LOGGED_IN_USER_TYPES;
  
    if (!meta?.customerId) {
      localStorage.setItem(userTypeKey, userTypes.superAdmin);
      return;
    }
    const userRole = permissions?.[USER_MGMT_CNST.CURRENT_USERMANAGEMENT_APPLICATION]?.roleName;
    const userType = userRole === USER_MGMT_CNST.CUSTOMER_ADMIN_ROLE
      ? userTypes.organizationAdmin
      : userTypes.organizationUser;
  
    localStorage.setItem(userTypeKey, userType);
  }

  getLoggedInUserType() : string {
    return localStorage.getItem(USER_MGMT_CNST.LOGGED_IN_USER_TYPE_KEY) as string;
  }

  setOrganizationInfo(organizationInfo: any) {
    localStorage.setItem(USER_MGMT_CNST.ORGANIZATION_INFO_KEY,JSON.stringify(organizationInfo))
  }
  
  getOrganizationInfo() : any {
    return JSON.parse(localStorage.getItem(USER_MGMT_CNST.ORGANIZATION_INFO_KEY) as string);
  }

  setApplicationInfo(applicationInfo: any) {
    localStorage.setItem(USER_MGMT_CNST.APPLICATION_INFO_KEY, JSON.stringify(applicationInfo))
  }

  getApplicationInfo() : any {
    return JSON.parse(localStorage?.getItem(USER_MGMT_CNST?.APPLICATION_INFO_KEY) as string);
  }

  removeApplicationInfo() {
    localStorage.removeItem(USER_MGMT_CNST.APPLICATION_INFO_KEY);
  }

  applicationDetails: any = {};
  applicationDetailsRef = new BehaviorSubject<any>(null);

  setApplicationDetails(value: any) {
    this.applicationDetails = value;
    this.setApplicationInfo(this.applicationDetails);
    this.applicationDetailsRef.next(this.applicationDetails);
  }

  getApplicationDetails() {
    return this.applicationDetailsRef.asObservable();
  }
  
  applicationList :any[] = [];
  applicationListRef = new Subject<any>;

  setApplicationList(data:any) {
    this.applicationList = data;
    this.applicationListRef.next(this.applicationList);
  } 

  getApplicationList() {
    return this.applicationList;
  }

  getUserRoleId() {
    return this.userPermissions?.[PROJECT_CONSTANTS.APPLICATION_NAME]?.['roleId'] ?? null;
  }

  setTriggerDetails(triggerDetails: any) {
    localStorage.setItem(USER_MGMT_CNST.TRIGGER_DETAILS_INFO_KEY, JSON.stringify(triggerDetails))
  }

  getTriggerDetails() : any {
    return JSON.parse(localStorage?.getItem(USER_MGMT_CNST?.TRIGGER_DETAILS_INFO_KEY) as string);
  }

  removeTriggerDetails() {
    localStorage.removeItem(USER_MGMT_CNST?.TRIGGER_DETAILS_INFO_KEY);
  }

  /**
   * Retrieves the user profile information.
   * Returns the cached profile if available, otherwise loads it from Keycloak.
   * @returns A promise resolving to the user profile object.
   */
  async getUserInfo(): Promise<any> {
    if(this.userProfile) {
      return this.userProfile; // Return the cached user profile if available
    }
    console.log("keyCloakInstance==", this.keyCloakInstance)
    const userProfile =  await this.keyCloakInstance.loadUserProfile();
    this.userProfile = userProfile;
    return userProfile;
  }

  setUserInfoInLocalStorage(userInfo: any) {
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
  }

  getUserInfoFromLocalStorage() {
    return JSON.parse(localStorage.getItem('userInfo') ?? '{}');
  }
}
